import React, { useState, useEffect } from 'react';
import { useOasisBackend } from '../../hooks/useOasisBackend';
import DatasetLibrary from '../components/Libraries/DatasetLibrary';
import Modal from '../components/modal';
import { useSearchParams } from 'react-router-dom';
import LineChart from '../components/charts/LineChart';
import { FullPageLoader } from '../components/loader';
import CreateVectorOntology from '../components/forms/CreateVectorOntology';

interface Dimension {
  id: number;
  origin: string;
  created_at: string;
  updated_at: string;
  dim_str: string;
  account: number;
}

interface VectorOntology {
  id: number;
  dimensions: Dimension[];
  created_at: string;
  updated_at: string;
  name: string;
  description: string;
  account: number;
}

interface LeaderboardEntry {
  id: number;
  account: number;
  user_name: string;
  reconstruction_reward: number;
  rank_reward: number;
  num_entities: number;
  num_queries: number;
  seed: number;
  vector_ontology: VectorOntology;
  dataset: number;
  created_at: string;
}

const LeaderBoard: React.FC = () => {
  const [selectedDataset, setSelectedDataset] = useState<any>(null);
  const [isDatasetModalOpen, setIsDatasetModalOpen] = useState(false);
  const [leaderboardData, setLeaderboardData] = useState<LeaderboardEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedPlayer, setSelectedPlayer] = useState<LeaderboardEntry | null>(null);
  const [showVectorOntologyForm, setShowVectorOntologyForm] = useState(false);
  const [showVectorOntologyDetails, setShowVectorOntologyDetails] = useState(false);
  const [sortConfig, setSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' }>({
    key: 'average_score',
    direction: 'desc'
  });
  const db = useOasisBackend();
  const [searchParams, setSearchParams] = useSearchParams();

  const fetchLeaderboard = async () => {
    try {
      const params = selectedDataset?.id ? { dataset_id: selectedDataset.id.toString() } : {};
      const response = await db.endpoints.testsuite.testsuiteApiTestsuiteGetOntologyLeaderboardCreate(params);
      if (response.data) {
        const data = response.data as any as LeaderboardEntry[];
        setLeaderboardData(data);
      }
    } catch (error) {
      console.error('Error fetching leaderboard:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const datasetId = searchParams.get('dataset_id');
    if (datasetId) {
      // Fetch dataset details if we have an ID
      db.endpoints.testsuite.testsuiteApiGetDatasetRetrieve(datasetId)
        .then((response: { data: any }) => {
          if (response.data) {
            setSelectedDataset(response.data);
          }
        })
        .catch((error: Error) => {
          console.error('Error fetching dataset:', error);
        });
    } else {
      fetchLeaderboard();
    }
  }, [searchParams, db.endpoints.testsuite]);

  useEffect(() => {
    if (selectedDataset) {
      fetchLeaderboard();
    }
  }, [selectedDataset, db.endpoints.testsuite]);

  const handleDatasetSelect = (dataset: any) => {
    setSelectedDataset(dataset);
    setSearchParams({ dataset_id: dataset.id.toString() });
    setIsDatasetModalOpen(false);
  };

  const getChartData = (player: LeaderboardEntry | null) => {
    if (!leaderboardData || leaderboardData.length === 0) {
      return [];
    }

    const timestamps = leaderboardData.map(entry => entry.created_at);
    const metrics = ['rank_reward', 'reconstruction_reward'];
    
    // If no player selected, show all players for each metric
    if (!player) {
      return metrics.map(metric => ({
        data: {
          xValues: timestamps,
          yValues: leaderboardData
            .map(entry => entry[metric as keyof LeaderboardEntry] as number)
            .filter((value): value is number => value !== null)
        },
        title: `${metric.replace('_', ' ').toUpperCase()} Over Time`,
        xAxisLabel: "Date",
        yAxisLabel: metric.replace('_', ' ').toUpperCase(),
        showLegend: false,
        lineColor: ['blue', 'red', 'green', 'purple'][metrics.indexOf(metric) % 4]
      }));
    }

    // If player selected, show their metrics
    return metrics.map(metric => ({
      data: {
        xValues: [player.created_at],
        yValues: [player[metric as keyof LeaderboardEntry] as number]
      },
      title: `${metric.replace('_', ' ').toUpperCase()} Over Time`,
      xAxisLabel: "Date",
      yAxisLabel: metric.replace('_', ' ').toUpperCase(),
      showLegend: false,
      lineColor: ['blue', 'red', 'green', 'purple'][metrics.indexOf(metric) % 4]
    }));
  };

  const getSortedData = () => {
    const sortedData = [...leaderboardData].map((entry, index) => ({
      ...entry,
      average_score: (entry.rank_reward + entry.reconstruction_reward) / 2
    }));

    const sorted = sortedData.sort((a, b) => {
      if (sortConfig.key === 'user_name') {
        return sortConfig.direction === 'asc' 
          ? a.user_name.localeCompare(b.user_name)
          : b.user_name.localeCompare(a.user_name);
      }

      const aValue = a[sortConfig.key as keyof typeof a];
      const bValue = b[sortConfig.key as keyof typeof b];

      if (sortConfig.direction === 'asc') {
        return aValue > bValue ? 1 : -1;
      }
      return aValue < bValue ? 1 : -1;
    });

    // Calculate ranks based on the sorted data
    let currentRank = 1;
    let currentValue: any = null;
    let rankCount = 0;

    return sorted.map((entry, index) => {
      const value = entry[sortConfig.key as keyof typeof entry];
      
      if (currentValue !== value) {
        currentRank = index + 1;
        currentValue = value;
        rankCount = 1;
      } else {
        rankCount++;
      }

      return {
        ...entry,
        rank: currentRank
      };
    });
  };

  const handleSort = (key: string) => {
    setSortConfig(current => ({
      key,
      direction: current.key === key && current.direction === 'desc' ? 'asc' : 'desc'
    }));
  };

  const getSortIcon = (key: string) => {
    if (sortConfig.key !== key) return '↕️';
    return sortConfig.direction === 'asc' ? '↑' : '↓';
  };

  if (isLoading) {
    return <FullPageLoader />;
  }

  return (
    <div className="mx-auto p-4 min-h-full flex flex-col bg-gray-900 text-white w-full">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Leaderboard</h1>
        <div className="flex gap-4">
          {selectedDataset && (
            <button
              onClick={() => setShowVectorOntologyForm(true)}
              className="bg-blue-500 hover:bg-blue-700 text-white px-4 py-2 rounded-lg"
            >
              Create Vector Ontology
            </button>
          )}
          <button
            onClick={() => setIsDatasetModalOpen(true)}
            className="bg-gray-700 text-white px-4 py-2 rounded-lg hover:bg-gray-600"
          >
            {selectedDataset ? `Dataset: ${selectedDataset.name}` : 'Select Dataset'}
          </button>
        </div>
      </div>

      <div className="flex gap-6 w-full">
        {/* Leaderboard Panel */}
        <div className="w-1/3 bg-gray-800 rounded-lg shadow-md overflow-hidden">
          <div className="grid grid-cols-5 gap-4 p-4 bg-gray-700 font-semibold">
            <div 
              className="truncate cursor-pointer hover:bg-gray-600 p-2 rounded"
              onClick={() => handleSort('rank')}
            >
              Rank {getSortIcon('rank')}
            </div>
            <div 
              className="truncate cursor-pointer hover:bg-gray-600 p-2 rounded"
              onClick={() => handleSort('user_name')}
            >
              User {getSortIcon('user_name')}
            </div>
            <div 
              className="truncate cursor-pointer hover:bg-gray-600 p-2 rounded"
              onClick={() => handleSort('rank_reward')}
            >
              Rank Reward {getSortIcon('rank_reward')}
            </div>
            <div 
              className="truncate cursor-pointer hover:bg-gray-600 p-2 rounded"
              onClick={() => handleSort('reconstruction_reward')}
            >
              Reconstruction Reward {getSortIcon('reconstruction_reward')}
            </div>
            <div 
              className="truncate cursor-pointer hover:bg-gray-600 p-2 rounded"
              onClick={() => handleSort('average_score')}
            >
              Average Score {getSortIcon('average_score')}
            </div>
          </div>
          <div className="divide-y divide-gray-700">
            {getSortedData().map((entry) => (
              <div 
                key={entry.id} 
                className={`grid grid-cols-5 gap-4 p-4 hover:bg-gray-700 cursor-pointer ${
                  selectedPlayer?.id === entry.id ? 'bg-blue-900' : ''
                }`}
                onClick={() => {
                  setSelectedPlayer(entry);
                  setShowVectorOntologyDetails(true);
                }}
              >
                <div className="font-semibold truncate">{entry.rank}</div>
                <div className="truncate">{entry.user_name}</div>
                <div className="truncate">{entry.rank_reward.toFixed(4)}</div>
                <div className="truncate">{entry.reconstruction_reward.toFixed(4)}</div>
                <div className="truncate">{((entry.rank_reward + entry.reconstruction_reward) / 2).toFixed(4)}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Graph Panel */}
        <div className="w-2/3 bg-gray-800 rounded-lg shadow-md p-4">
          <div className="h-[500px]">
            <h2 className="text-xl mb-4">
              {selectedPlayer 
                ? `${selectedPlayer.user_name}'s Performance History`
                : 'All Players Performance History'}
            </h2>
            <div className="grid grid-cols-2 gap-4">
              {getChartData(selectedPlayer).map((chartProps, index) => (
                <div key={index} className="h-[200px]">
                  <LineChart {...chartProps} />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Vector Ontology Details Modal */}
      <Modal
        isOpen={showVectorOntologyDetails}
        onClose={() => setShowVectorOntologyDetails(false)}
        size="medium"
      >
        {selectedPlayer && (
          <div className="p-6 bg-gray-800 text-white">
            <h2 className="text-2xl font-bold mb-4">Vector Ontology Details</h2>
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-semibold mb-2">Name</h3>
                <p className="text-gray-200">{selectedPlayer.vector_ontology.name}</p>
              </div>
              <div>
                <h3 className="text-lg font-semibold mb-2">Description</h3>
                <p className="text-gray-200">{selectedPlayer.vector_ontology.description}</p>
              </div>
              <div>
                <h3 className="text-lg font-semibold mb-2">Dimensions</h3>
                <ul className="list-disc list-inside space-y-2">
                  {selectedPlayer.vector_ontology.dimensions.map((dimension) => (
                    <li key={dimension.id} className="text-gray-200">
                      {dimension.dim_str} ({dimension.origin})
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <h3 className="text-lg font-semibold mb-2">Metrics</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-gray-700 p-3 rounded">
                    <div className="font-medium">Rank Reward</div>
                    <div className="text-gray-200">{selectedPlayer.rank_reward.toFixed(4)}</div>
                  </div>
                  <div className="bg-gray-700 p-3 rounded">
                    <div className="font-medium">Reconstruction Reward</div>
                    <div className="text-gray-200">{selectedPlayer.reconstruction_reward.toFixed(4)}</div>
                  </div>
                  <div className="bg-gray-700 p-3 rounded">
                    <div className="font-medium">Number of Entities</div>
                    <div className="text-gray-200">{selectedPlayer.num_entities}</div>
                  </div>
                  <div className="bg-gray-700 p-3 rounded">
                    <div className="font-medium">Number of Queries</div>
                    <div className="text-gray-200">{selectedPlayer.num_queries}</div>
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <h3 className="text-lg font-semibold mb-2">Created</h3>
                  <p className="text-gray-200">
                    {new Date(selectedPlayer.vector_ontology.created_at).toLocaleString()}
                  </p>
                </div>
                <div>
                  <h3 className="text-lg font-semibold mb-2">Last Updated</h3>
                  <p className="text-gray-200">
                    {new Date(selectedPlayer.vector_ontology.updated_at).toLocaleString()}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </Modal>

      {/* Vector Ontology Form Modal */}
      <Modal
        isOpen={showVectorOntologyForm}
        onClose={() => setShowVectorOntologyForm(false)}
        size="large"
      >
        <div className="p-4 bg-gray-800 text-white">
          <h2 className="text-2xl font-bold mb-4">Create Vector Ontology</h2>
          <CreateVectorOntology
            datasetId={selectedDataset?.id}
            onSuccess={() => {
              setShowVectorOntologyForm(false);
              fetchLeaderboard();
            }}
            onCancel={() => setShowVectorOntologyForm(false)}
          />
        </div>
      </Modal>

      {/* Dataset Modal */}
      <Modal
        isOpen={isDatasetModalOpen}
        onClose={() => setIsDatasetModalOpen(false)}
        size="large"
      >
        <div className="bg-gray-800 text-white">
          <DatasetLibrary
            onSelect={handleDatasetSelect}
            onCancel={() => setIsDatasetModalOpen(false)}
          />
        </div>
      </Modal>
    </div>
  );
};

export default LeaderBoard;
